// ---------------------------------------------------------------------------
// JOB ALERT UNSUBSCRIBE PAGE  (/alerts/unsubscribe?token=...)
// ---------------------------------------------------------------------------
// The link in every digest email points here. The page calls the public
// `job-alerts-unsubscribe` edge function with the capability token, which flips
// that ONE saved search to active = false.
//
// Deliberate behaviour:
//   * No login required — a contractor must be able to stop email from the
//     inbox, without hunting for a password.
//   * Nothing is deleted, and no other alert, receipt or account email is
//     affected. The copy says so plainly instead of implying a full opt-out.
//   * noindex: this is a one-off capability URL, never a search result.
// ---------------------------------------------------------------------------
import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BellOff, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';

type State = 'working' | 'done' | 'failed';

const AlertUnsubscribe: React.FC = () => {
  const { t, path } = useI18n();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState<State>('working');
  const [name, setName] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState('failed');
      return;
    }
    supabase.functions
      .invoke('job-alerts-unsubscribe', { body: { token } })
      .then(({ data, error }) => {
        if (error || !data?.success) {
          setState('failed');
          return;
        }
        setName(String(data.name || ''));
        setState('done');
      })
      .catch(() => setState('failed'));
  }, [token]);

  return (
    <>
      <Seo title={t('alerts.unsubTitle')} description={t('alerts.unsubBody')} noindex />
      <div className="mx-auto max-w-xl px-4 py-16">
        <div className="rounded-2xl border border-slate-200 bg-white p-7">
          {state === 'working' && (
            <p className="flex items-center gap-2 text-slate-600">
              <Loader2 className="h-5 w-5 animate-spin text-orange-500" /> {t('alerts.unsubWorking')}
            </p>
          )}

          {state === 'done' && (
            <>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100">
                <BellOff className="h-5 w-5 text-slate-700" />
              </div>
              <h1 className="mt-4 text-2xl font-extrabold text-slate-900">{t('alerts.unsubTitle')}</h1>
              {name && <p className="mt-1 text-sm font-bold text-slate-700">{name}</p>}
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{t('alerts.unsubBody')}</p>
            </>
          )}

          {state === 'failed' && (
            <>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <h1 className="mt-4 text-2xl font-extrabold text-slate-900">{t('alerts.unsubFail')}</h1>
            </>
          )}

          {state !== 'working' && (
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={path('/dashboard')}
                className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold text-white hover:bg-orange-600"
              >
                {t('alerts.manage')}
              </Link>
              <Link
                to={path('/jobs')}
                className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:border-slate-400"
              >
                {t('home.ctaBrowseProjects')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AlertUnsubscribe;
